import React, { useState } from 'react';
import { toast, Toaster } from 'sonner';
import '../../HomePage.css';

// ─── SVG Clock Icon ───────────────────────────────────────────────
const TymeClockIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="#22c77a" strokeWidth="1.5" strokeLinecap="round" width={28} height={28}>
    <circle cx="8" cy="8" r="6.5" />
    <path d="M8 4.5V8l2.5 1.5" />
  </svg>
);

// ─── TERMS content ────────────────────────────────────────────────
const TermsContent = () => (
  <div className="hp-legal-scroll">
    <p><strong>Effective date:</strong> 3 October 2025</p>
    <p><strong>Company:</strong> Tyme ("we", "us", "our")</p>
    <p><strong>Contact:</strong> tymeeltd@gmail.com</p>
    <p>By accessing or using Tyme's website and services, you agree to the following Terms &amp; Conditions. If you do not agree, you must not use our services.</p>
    <h3>1. Use of Service</h3>
    <p>Tyme provides cloud-based staff attendance and time-tracking tools. You agree to use the service only for lawful purposes and in accordance with these terms.</p>
    <p>You are responsible for maintaining the confidentiality of your account login details and for all activities under your account.</p>
    <h3>2. Free Trial and Subscription</h3>
    <ul><li>We offer a 3-day free trial on the Pro Plan.</li><li>After the trial, your subscription will automatically convert to a paid monthly plan unless you cancel before the trial period ends.</li><li>You can cancel your subscription at any time from your account dashboard.</li><li>Subscription fees are charged monthly in advance and are non-refundable once the billing period starts.</li></ul>
    <h3>3. Payment and Billing</h3>
    <ul><li>All payments are processed securely through our payment provider.</li><li>You are responsible for providing accurate billing information and ensuring sufficient funds.</li><li>We reserve the right to suspend or terminate your account if payment fails.</li></ul>
    <h3>4. Intellectual Property</h3>
    <p>All content, software, logos, and materials provided by Tyme are owned by us or licensed to us. You may not copy, distribute, modify, or use them for commercial purposes without our prior written consent.</p>
    <h3>5. Service Availability</h3>
    <p>We aim to provide uninterrupted access to our services, but we do not guarantee that the platform will be error-free or available 100% of the time. We may temporarily suspend services for maintenance or updates.</p>
    <h3>6. Limitation of Liability</h3>
    <p>To the fullest extent permitted by law, Tyme is not liable for:</p>
    <ul><li>Any indirect, incidental, or consequential damages arising from the use of our services.</li><li>Any data loss, unauthorised access, or service interruption beyond our reasonable control.</li></ul>
    <h3>7. Use of Artificial Intelligence (AI)</h3>
    <p>Tyme incorporates AI-powered tools and features to support and enhance the functionality of our services.</p>
    <p>By using Tyme, you acknowledge and agree that:</p>
    <ul><li>AI-powered outputs are generated based on available data and algorithms and are provided for informational and operational purposes only.</li><li>Tyme does not guarantee the accuracy, completeness, or suitability of any AI-generated suggestions or insights.</li><li>You remain solely responsible for any decisions or actions taken based on AI-assisted outputs.</li></ul>
    <p>We are committed to ensuring that any AI features are deployed ethically, securely, and in compliance with relevant data-protection and privacy laws.</p>
    <h3>8. Termination</h3>
    <p>We reserve the right to suspend or terminate your account if you violate these Terms &amp; Conditions or misuse our services.</p>
    <h3>9. Changes to These Terms</h3>
    <p>We may update these Terms from time to time. Continued use of Tyme after changes means you accept the revised terms.</p>
    <h3>10. Governing Law</h3>
    <p>These Terms &amp; Conditions are governed by and construed in accordance with the laws of England and Wales, and any disputes shall be subject to the exclusive jurisdiction of UK courts.</p>
    <div className="hp-legal-contact"><p><strong>Contact Us:</strong></p><p>For questions about these Terms or our Privacy Policy, email us at: <a href="mailto:tymeeltd@gmail.com">tymeeltd@gmail.com</a></p></div>
  </div>
);

// ─── PRIVACY content ──────────────────────────────────────────────
const PrivacyContent = () => (
  <div className="hp-legal-scroll">
    <p><strong>Effective date:</strong> 3 October 2025</p>
    <p><strong>Company:</strong> Tyme ("we", "us", "our")</p>
    <p><strong>Contact:</strong> tymeeltd@gmail.com</p>
    <p>At Tyme, we are committed to protecting your privacy and handling your personal data responsibly and transparently. This Privacy Policy explains how we collect, use, store, and protect your information when you use our website and services.</p>
    <h3>1. Information We Collect</h3>
    <p>We collect and process the following types of personal data:</p>
    <ul><li>Email addresses – when you sign up for an account or subscribe to our services.</li><li>Device and cookie data – collected automatically when you visit our website to help us improve performance, security, and user experience.</li><li>Payment details – processed securely via our payment partner when you subscribe to a paid plan.</li></ul>
    <p>We do not collect location data, PIN codes, passwords, or unnecessary personal information.</p>
    <h3>2. How We Use Your Data</h3>
    <p>We use the information we collect for the following purposes:</p>
    <ul><li>To create and manage your Tyme account.</li><li>To provide and maintain our staff attendance and time-tracking services.</li><li>To communicate with you about updates, support, and changes to our services.</li><li>To process payments and manage your subscription.</li><li>To improve and optimise our website and platform functionality.</li></ul>
    <p>We will never sell or share your data with third parties for marketing purposes.</p>
    <h3>3. Legal Basis for Processing</h3>
    <p>We process your personal data under the following legal grounds:</p>
    <ul><li>Contract: to provide the service you signed up for.</li><li>Consent: when you agree to receive optional marketing emails.</li><li>Legitimate interests: for service improvement and security.</li></ul>
    <h3>4. Cookies</h3>
    <p>Tyme uses cookies and similar technologies to:</p>
    <ul><li>Analyse site traffic and usage.</li><li>Enhance site functionality.</li><li>Personalise your experience.</li></ul>
    <p>You can manage or disable cookies in your browser settings, but some features may not work properly if you do so.</p>
    <h3>5. Data Retention</h3>
    <p>We keep your personal data only as long as necessary:</p>
    <ul><li>Account data is retained while your account is active.</li><li>If you close your account, we will delete or anonymise your data within 30 days unless required to keep it by law.</li></ul>
    <h3>6. Your Rights</h3>
    <p>Under UK GDPR, you have the right to:</p>
    <ul><li>Access the personal data we hold about you.</li><li>Request correction or deletion of your data.</li><li>Withdraw consent for marketing.</li><li>Request data portability.</li><li>Lodge a complaint with the ICO (Information Commissioner's Office).</li></ul>
    <p>To exercise these rights, contact us at tymeeltd@gmail.com.</p>
    <h3>7. Security</h3>
    <p>We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, loss, misuse, or disclosure.</p>
    <h3>8. Use of Artificial Intelligence (AI)</h3>
    <p>Tyme uses artificial intelligence (AI) technologies within certain parts of our platform to enhance functionality and improve user experience.</p>
    <ul><li>No personal data is sold, shared, or used to train external AI models.</li><li>All AI processing complies with UK GDPR, and your data remains protected by the same security standards applied elsewhere in our platform.</li><li>Any data used for internal AI model training or service improvement is anonymised or aggregated wherever possible.</li></ul>
    <p>By using Tyme, you consent to the use of AI in accordance with this Privacy Policy.</p>
    <h3>9. Changes to This Policy</h3>
    <p>We may update this Privacy Policy from time to time. Any changes will be posted on this page with a new "effective date".</p>
    <div className="hp-legal-contact"><p><strong>Contact Us:</strong></p><p>For questions about our Privacy Policy, email us at: <a href="mailto:tymeeltd@gmail.com">tymeeltd@gmail.com</a></p></div>
  </div>
);

// =====================================================================
// AUTH SCREENS COMPONENT
// =====================================================================
const AuthScreens = ({
  // Form state
  loginForm, setLoginForm,
  signupForm, setSignupForm,
  verificationForm, setVerificationForm,
  forgotPasswordForm, setForgotPasswordForm,
  forgotPasswordStep, setForgotPasswordStep,
  forgotPasswordMessage,
  // View state
  showEmailVerification, setShowEmailVerification,
  showSignup, setShowSignup,
  showPaymentRequired, setShowPaymentRequired,
  showForgotPassword, setShowForgotPassword,
  showTermsModal, setShowTermsModal,
  showPrivacyModal, setShowPrivacyModal,
  // Loading & validation
  isLoading,
  passwordErrors, setPasswordErrors,
  nameError, setNameError,
  emailError, setEmailError,
  validatePassword, validateFullName, validateEmail,
  // Handlers
  handleLogin,
  handleSignup,
  handleVerifyEmail,
  handleResendCode,
  handleForgotPasswordSendCode,
  handleForgotPasswordReset,
  getStripePaymentUrl,
}) => {
  // ─── PAYMENT REQUIRED ────────────────────────────────────
  if (showPaymentRequired) {
    return (
      <div className="hp-root" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 900, padding: '40px 20px' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 16 }}>
              <div className="hp-logo-icon"><TymeClockIcon /></div>
              <span style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Tyme</span>
            </div>
            <h1 style={{ fontFamily: 'var(--display)', fontSize: 32, fontWeight: 700, color: 'var(--green)', marginBottom: 8 }}>Select Your Plan to Continue</h1>
            <p style={{ fontSize: 15, color: 'var(--text-2)' }}>You must purchase a plan to access your dashboard</p>
          </div>

          {/* Plans grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 14 }}>
            {[
              { name: 'Starter', price: '£49', desc: 'Up to 15 staff. PIN clock-in, live dashboard, leave management, hours reporting.', plan: 'starter' },
              { name: 'Growth', price: '£69', desc: 'Up to 30 staff. Everything in Starter plus Rota builder and more.', plan: 'growth', featured: true },
              { name: 'Pro', price: '£99', desc: 'Up to 60 staff. Everything in Growth plus advanced attendance rules.', plan: 'pro' },
            ].map((p) => (
              <div key={p.plan} className={`hp-plan ${p.featured ? 'featured' : ''}`}>
                <div className="hp-plan-name">{p.name}</div>
                <div className="hp-plan-price">{p.price}<span className="per"> / month</span></div>
                <p className="hp-plan-desc">{p.desc}</p>
                <button
                  className="hp-plan-btn"
                  data-testid={`payment-plan-${p.plan}-btn`}
                  onClick={async () => {
                    const stripeUrl = await getStripePaymentUrl(p.plan);
                    if (stripeUrl) window.location.href = stripeUrl;
                  }}
                >
                  Choose {p.name}
                </button>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <p style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 16 }}>After purchase, log back in to access your dashboard.</p>
            <button className="hp-btn-ghost-sm" onClick={() => setShowPaymentRequired(false)}>← Back to Login</button>
          </div>
        </div>
        <Toaster position="top-center" toastOptions={{ style: { background: '#1a1a1f', color: '#e8e8ec', border: '1px solid rgba(255,255,255,0.12)', fontSize: '14px' } }} />
      </div>
    );
  }

  // ─── EMAIL VERIFICATION ──────────────────────────────────
  if (showEmailVerification) {
    return (
      <div className="hp-root" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 400, padding: '0 20px' }}>
          {/* Brand */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 16 }}>
              <div className="hp-logo-icon"><TymeClockIcon /></div>
              <span style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Tyme</span>
            </div>
            <h1 style={{ fontFamily: 'var(--display)', fontSize: 26, fontWeight: 700, color: 'var(--green)', marginBottom: 6 }}>Verify Your Email</h1>
            <p style={{ fontSize: 14, color: 'var(--text-2)' }}>Step 2 of 2 — Almost there!</p>
          </div>

          <div className="hp-modal" style={{ maxWidth: '100%', animation: 'none' }}>
            {/* Email icon */}
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '50%', background: 'var(--green-bg)', border: '1px solid rgba(34,199,122,0.25)', marginBottom: 12 }}>
                <svg width="24" height="24" fill="none" stroke="#22c77a" strokeWidth="1.5" strokeLinecap="round" viewBox="0 0 24 24">
                  <rect x="2" y="4" width="20" height="16" rx="3" />
                  <path d="M2 7l10 7 10-7" />
                </svg>
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-2)' }}>We sent a 6-digit code to</p>
              <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginTop: 4 }}>{verificationForm.email}</p>
              <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>Check your inbox (and spam folder)</p>
            </div>

            <form onSubmit={handleVerifyEmail}>
              <div className="hp-modal-fields">
                <div className="hp-modal-field">
                  <div className="hp-modal-label">Verification code</div>
                  <input
                    className="hp-modal-input"
                    data-testid="auth-verify-code"
                    type="text"
                    placeholder="000000"
                    maxLength={6}
                    value={verificationForm.verification_code}
                    onChange={(e) => setVerificationForm({
                      ...verificationForm,
                      verification_code: e.target.value.replace(/\D/g, '').slice(0, 6)
                    })}
                    required
                    autoFocus
                    style={{ textAlign: 'center', letterSpacing: '0.4em', fontFamily: 'var(--mono)', fontSize: 20 }}
                  />
                </div>
              </div>
              <button
                className="hp-modal-submit"
                data-testid="auth-verify-submit"
                type="submit"
                disabled={isLoading || verificationForm.verification_code.length !== 6}
                style={{ marginTop: 14 }}
              >
                {isLoading ? 'Verifying...' : 'Verify & Create Account'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <a style={{ fontSize: 12, color: 'var(--green)', cursor: 'pointer' }} onClick={handleResendCode}>
                Didn't receive the code? Resend
              </a>
            </div>

            <div className="hp-modal-footer">
              <a onClick={() => { setShowEmailVerification(false); setShowSignup(true); }}>← Back to signup</a>
            </div>

            <div style={{ marginTop: 12, padding: 12, background: 'var(--green-bg)', borderRadius: 10, border: '1px solid rgba(34,199,122,0.2)', textAlign: 'center' }}>
              <p style={{ fontSize: 12, color: 'var(--green)' }}>
                After verification, you'll start with a free Starter plan. Upgrade anytime.
              </p>
            </div>
          </div>
        </div>
        <Toaster position="top-center" toastOptions={{ style: { background: '#1a1a1f', color: '#e8e8ec', border: '1px solid rgba(255,255,255,0.12)', fontSize: '14px' } }} />
      </div>
    );
  }

  // ─── SIGNUP ──────────────────────────────────────────────
  if (showSignup) {
    return (
      <div className="hp-root" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          {/* Brand */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 14 }}>
              <div className="hp-logo-icon"><TymeClockIcon /></div>
              <span style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Tyme</span>
            </div>
            <h1 style={{ fontFamily: 'var(--display)', fontSize: 26, fontWeight: 700, color: 'var(--green)', marginBottom: 6 }}>Create Account</h1>
            <p style={{ fontSize: 14, color: 'var(--text-2)' }}>Join thousands of businesses using Tyme</p>
          </div>

          <div className="hp-modal wide" style={{ maxWidth: '100%', animation: 'none' }}>
            <form onSubmit={handleSignup}>
              <div className="hp-modal-fields">
                <div className="hp-modal-field">
                  <div className="hp-modal-label">Full name</div>
                  <input
                    className="hp-modal-input"
                    data-testid="auth-signup-name"
                    type="text"
                    placeholder="Jane Smith"
                    value={signupForm.name}
                    onChange={(e) => { setSignupForm({ ...signupForm, name: e.target.value }); setNameError(''); }}
                    required
                  />
                  {nameError && <div style={{ marginTop: 4, fontSize: 11, color: '#e85252' }}>{nameError}</div>}
                </div>
                <div className="hp-modal-field">
                  <div className="hp-modal-label">Email</div>
                  <input
                    className="hp-modal-input"
                    data-testid="auth-signup-email"
                    type="email"
                    placeholder="you@company.com"
                    value={signupForm.email}
                    onChange={(e) => {
                      setSignupForm({ ...signupForm, email: e.target.value });
                      setEmailError('');
                      if (signupForm.password) setPasswordErrors(validatePassword(signupForm.password));
                    }}
                    required
                  />
                  {emailError && <div style={{ marginTop: 4, fontSize: 11, color: '#e85252' }}>{emailError}</div>}
                </div>
                <div className="hp-modal-field">
                  <div className="hp-modal-label">Password</div>
                  <input
                    className="hp-modal-input"
                    data-testid="auth-signup-password"
                    type="password"
                    placeholder="Create a strong password"
                    value={signupForm.password}
                    onChange={(e) => {
                      setSignupForm({ ...signupForm, password: e.target.value });
                      setPasswordErrors(validatePassword(e.target.value));
                    }}
                    required
                  />
                  {passwordErrors.length > 0 && (
                    <div className="hp-pw-errors">{passwordErrors.map((err, i) => <span key={i}>• {err}</span>)}</div>
                  )}
                </div>
                <div className="hp-modal-field">
                  <div className="hp-modal-label">Confirm password</div>
                  <input
                    className="hp-modal-input"
                    data-testid="auth-signup-confirm"
                    type="password"
                    placeholder="Confirm your password"
                    value={signupForm.confirm_password}
                    onChange={(e) => setSignupForm({ ...signupForm, confirm_password: e.target.value })}
                    required
                  />
                </div>
                {/* T&C and Privacy checkboxes */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                  <label className="hp-checkbox-row">
                    <input type="checkbox" checked={signupForm.acceptTerms} onChange={(e) => setSignupForm({ ...signupForm, acceptTerms: e.target.checked })} data-testid="auth-signup-terms" />
                    <span>I agree to the <a onClick={(ev) => { ev.preventDefault(); setShowTermsModal(true); }}>Terms &amp; Conditions</a></span>
                  </label>
                  <label className="hp-checkbox-row">
                    <input type="checkbox" checked={signupForm.acceptPrivacy} onChange={(e) => setSignupForm({ ...signupForm, acceptPrivacy: e.target.checked })} data-testid="auth-signup-privacy" />
                    <span>I agree to the <a onClick={(ev) => { ev.preventDefault(); setShowPrivacyModal(true); }}>Privacy Policy</a></span>
                  </label>
                </div>
              </div>
              <button className="hp-modal-submit" data-testid="auth-signup-submit" type="submit" disabled={isLoading || !signupForm.acceptTerms || !signupForm.acceptPrivacy} style={{ marginTop: 14, opacity: (!signupForm.acceptTerms || !signupForm.acceptPrivacy) ? 0.45 : 1, cursor: (!signupForm.acceptTerms || !signupForm.acceptPrivacy) ? 'not-allowed' : 'pointer' }}>
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            <div className="hp-modal-footer">
              Already have an account? <a data-testid="auth-signup-to-login" onClick={() => setShowSignup(false)}>Sign in</a>
            </div>
          </div>
        </div>

        {/* Terms Modal */}
        <div className={`hp-modal-overlay ${showTermsModal ? 'open' : ''}`} onClick={(e) => e.target === e.currentTarget && setShowTermsModal(false)}>
          <div className="hp-modal hp-legal-modal">
            <div className="hp-modal-header">
              <div className="hp-modal-title">Terms &amp; Conditions</div>
              <button className="hp-modal-close" onClick={() => setShowTermsModal(false)}>✕</button>
            </div>
            <TermsContent />
            <button className="hp-modal-submit" onClick={() => { setSignupForm({ ...signupForm, acceptTerms: true }); setShowTermsModal(false); }}>I Agree to Terms &amp; Conditions</button>
          </div>
        </div>

        {/* Privacy Modal */}
        <div className={`hp-modal-overlay ${showPrivacyModal ? 'open' : ''}`} onClick={(e) => e.target === e.currentTarget && setShowPrivacyModal(false)}>
          <div className="hp-modal hp-legal-modal">
            <div className="hp-modal-header">
              <div className="hp-modal-title">Privacy Policy</div>
              <button className="hp-modal-close" onClick={() => setShowPrivacyModal(false)}>✕</button>
            </div>
            <PrivacyContent />
            <button className="hp-modal-submit" onClick={() => { setSignupForm({ ...signupForm, acceptPrivacy: true }); setShowPrivacyModal(false); }}>I Agree to Privacy Policy</button>
          </div>
        </div>

        <Toaster position="top-center" toastOptions={{ style: { background: '#1a1a1f', color: '#e8e8ec', border: '1px solid rgba(255,255,255,0.12)', fontSize: '14px' } }} />
      </div>
    );
  }

  // ─── LOGIN (default) ─────────────────────────────────────
  return (
    <div className="hp-root" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 14 }}>
            <div className="hp-logo-icon"><TymeClockIcon /></div>
            <span style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Tyme</span>
          </div>
          <h1 style={{ fontFamily: 'var(--display)', fontSize: 26, fontWeight: 700, color: 'var(--green)', marginBottom: 6 }} data-testid="auth-login-heading">Welcome Back</h1>
          <p style={{ fontSize: 14, color: 'var(--text-2)' }}>Sign in to access your dashboard</p>
        </div>

        <div className="hp-modal" style={{ maxWidth: '100%', animation: 'none' }}>
          <form onSubmit={handleLogin}>
            <div className="hp-modal-fields">
              <div className="hp-modal-field">
                <div className="hp-modal-label">Email</div>
                <input
                  className="hp-modal-input"
                  data-testid="auth-login-email"
                  type="text"
                  placeholder="you@company.com"
                  value={loginForm.email_or_name}
                  onChange={(e) => setLoginForm({ ...loginForm, email_or_name: e.target.value })}
                  required
                />
              </div>
              <div className="hp-modal-field">
                <div className="hp-modal-label">Password</div>
                <input
                  className="hp-modal-input"
                  data-testid="auth-login-password"
                  type="password"
                  placeholder="Enter your password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  required
                />
              </div>
            </div>
            <button className="hp-modal-submit" data-testid="auth-login-submit" type="submit" disabled={isLoading} style={{ marginTop: 14 }}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <a style={{ fontSize: 12, color: 'var(--text-3)', cursor: 'pointer' }} onClick={() => setShowForgotPassword(true)}>
              Forgot Password?
            </a>
          </div>

          <div className="hp-modal-footer">
            Don't have an account? <a data-testid="auth-login-to-signup" onClick={() => setShowSignup(true)}>Create Account</a>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <div className={`hp-modal-overlay ${showForgotPassword ? 'open' : ''}`} onClick={(e) => e.target === e.currentTarget && setShowForgotPassword(false)}>
        <div className="hp-modal">
          <div className="hp-modal-header">
            <div className="hp-modal-title">
              {forgotPasswordStep === 'email' ? 'Reset Password' : 'Enter Verification Code'}
            </div>
            <button className="hp-modal-close" onClick={() => { setShowForgotPassword(false); setForgotPasswordStep('email'); }}>✕</button>
          </div>

          {forgotPasswordStep === 'email' ? (
            <form onSubmit={handleForgotPasswordSendCode}>
              <div className="hp-modal-fields">
                <div className="hp-modal-field">
                  <div className="hp-modal-label">Email</div>
                  <input
                    className="hp-modal-input"
                    type="email"
                    placeholder="you@company.com"
                    value={forgotPasswordForm.email}
                    onChange={(e) => setForgotPasswordForm({ ...forgotPasswordForm, email: e.target.value })}
                    required
                  />
                </div>
              </div>
              <button className="hp-modal-submit" type="submit" disabled={isLoading} style={{ marginTop: 14 }}>
                {isLoading ? 'Sending...' : 'Send Reset Code'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleForgotPasswordReset}>
              <div className="hp-modal-fields">
                <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-2)', marginBottom: 4 }}>
                  We've sent a 6-digit code to your email address.
                </div>
                <div className="hp-modal-field">
                  <div className="hp-modal-label">Verification code</div>
                  <input
                    className="hp-modal-input"
                    type="text"
                    placeholder="000000"
                    maxLength={6}
                    value={forgotPasswordForm.verification_code}
                    onChange={(e) => setForgotPasswordForm({ ...forgotPasswordForm, verification_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    required
                    style={{ textAlign: 'center', letterSpacing: '0.3em', fontFamily: 'var(--mono)' }}
                  />
                </div>
                <div className="hp-modal-field">
                  <div className="hp-modal-label">New password</div>
                  <input
                    className="hp-modal-input"
                    type="password"
                    placeholder="Enter new password"
                    value={forgotPasswordForm.new_password}
                    onChange={(e) => setForgotPasswordForm({ ...forgotPasswordForm, new_password: e.target.value })}
                    required
                  />
                </div>
                <div className="hp-modal-field">
                  <div className="hp-modal-label">Confirm new password</div>
                  <input
                    className="hp-modal-input"
                    type="password"
                    placeholder="Confirm new password"
                    value={forgotPasswordForm.confirm_password}
                    onChange={(e) => setForgotPasswordForm({ ...forgotPasswordForm, confirm_password: e.target.value })}
                    required
                  />
                </div>
              </div>
              <button className="hp-modal-submit" type="submit" disabled={isLoading} style={{ marginTop: 14 }}>
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}

          {forgotPasswordMessage && (
            <div className="hp-info">{forgotPasswordMessage}</div>
          )}

          <div className="hp-modal-footer">
            <a onClick={() => { setShowForgotPassword(false); setForgotPasswordStep('email'); }}>← Back to login</a>
          </div>
        </div>
      </div>

      <Toaster position="top-center" toastOptions={{ style: { background: '#1a1a1f', color: '#e8e8ec', border: '1px solid rgba(255,255,255,0.12)', fontSize: '14px' } }} />
    </div>
  );
};

export default AuthScreens;
